import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";

import AutoFormDialog from "../dialogTypes/AutoFormDialog";
import validateConditionJsonStructure from "../../lib/utils/validateConditionJsonStructure";
import CreateRecordButtonLayout from "../layout/CreateRecordButtonLayout";
/**
 * @COMPONENT_NAME MachineTypeStateTransitionCreateButton
 * @DESCRIPTION 설비 유형별 상태 천이 데이터 생성을 위한 커스텀 버튼 컴포넌트
 *             선택된 설비 유형에 상태 천이 데이터를 생성할 수 있는 다이얼로그를 표시
 *
 * @PROPS
 *   - {string} id - 컴포넌트 ID
 *   - {Object} schema - 테이블 스키마 정보
 *   - {Object} refresh - API refresh 객체
 *   - {Function} onSuccess - 성공 시 콜백
 *   - {Object} selectedMachineType - 선택된 설비 유형 데이터
 *   - {Array} machineTypeContextDataNames - 설비 유형 컨텍스트 데이터 이름 배열
 *   - {...any} others - 기타 props
 *
 * @CALLBACKS
 *   - {Function} onOpen - 버튼 클릭 시 호출되는 콜백
 *   - {Function} onClose - 다이얼로그 닫기 시 호출되는 콜백
 *   - {Function} onCreateSuccess - 생성 성공 시 호출되는 콜백
 *   - {Function} setInitialDataCallback - 초기 데이터 설정 콜백
 *   - {Function} checkFormValidationCallback - 폼 데이터 검증 콜백
 *
 */
function MachineTypeStateTransitionCreateButton(props) {
    const { t } = useTranslation();

    const { id, schema, refresh, onSuccess, selectedMachineType, machineTypeContextDataNames, ...others } = props;

    const [open, setOpen] = useState(false);
    const [modifiedSchema, setModifiedSchema] = useState();
    const [initialFormData, setInitialFormData] = useState();

    useEffect(() => {
        if (schema && selectedMachineType) {
            const newSchema = cloneDeep(schema);

            for (let column of newSchema.columns) {
                if (column.name === "prevState" || column.name === "nextState") {
                    if (column.codeInfo) {
                        column.codeInfo.tableName = `VwMachineTypeState/${selectedMachineType.machineTypeCode}`;
                    }
                }
            }

            setModifiedSchema(newSchema);
        }
    }, [schema, selectedMachineType]);

    const onOpen = () => {
        setOpen(true);
        setInitialFormData(undefined);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onCreateSuccess = (msg) => {
        if (onSuccess) {
            onSuccess(msg);
        }

        if (refresh) {
            refresh.refresh();
        }
    };

    const setInitialDataCallback = (formData) => {
        if (initialFormData) {
            for (let key in initialFormData) {
                if (key === "condition") continue;
                formData[key] = initialFormData[key];
            }
        }

        if (selectedMachineType) {
            formData["machineTypeCode"] = selectedMachineType.machineTypeCode;
        }
    };

    const modifyFormDataCallback = (formData, name, value) => {
        if (name !== "stateTransitionType") {
            return;
        }

        setInitialFormData(formData);

        if (formData.stateTransitionType === "EVENT") {
            const newSchema = cloneDeep(modifiedSchema);

            for (let column of newSchema.columns) {
                if (column.name === "condition") {
                    column.formItemType = "autocomplete";
                    column.codeInfo = {
                        tableName: `VwMachineTypeEvent/${selectedMachineType.machineTypeCode}`,
                        nameColumn: ["eventName", "description"],
                        valueColumn: "eventName",
                        useEmptyValue: false,
                        sortByName: true,
                    };
                }
            }
            setModifiedSchema(newSchema);
        } else if (formData.stateTransitionType === "CONDITION") {
            const newSchema = cloneDeep(modifiedSchema);

            for (let column of newSchema.columns) {
                if (column.name === "condition") {
                    column.formItemType = "text";
                }
            }

            setModifiedSchema(newSchema);
        }
    };

    const checkFormValidationCallback = (formData) => {
        if (formData.stateTransitionType === "EVENT") {
            return [true, ""];
        }

        if (formData.stateTransitionType === "CONDITION") {
            try {
                validateConditionJsonStructure(formData.condition, machineTypeContextDataNames);
            } catch (error) {
                return [false, error.message];
            }
        }

        return [true, ""];
    };

    return (
        <>
            <CreateRecordButtonLayout id={id + "create-button"} onClick={onOpen} {...others} />
            <AutoFormDialog
                id={id + "create-dialog"}
                mode="create"
                formId={id + "create-form"}
                title={schema ? t(`dialog.title.${schema.name}.create`) : ""}
                schema={modifiedSchema}
                initialData={undefined}
                open={open}
                onClose={onClose}
                onSuccess={onCreateSuccess}
                setInitialDataCallback={setInitialDataCallback}
                modifyFormDataCallback={modifyFormDataCallback}
                checkFormValidationCallback={checkFormValidationCallback}
            />
        </>
    );
}

export default MachineTypeStateTransitionCreateButton;
